import { ApiService } from "../../../../Services/ApiService";
import { IMultiSectionUnitConfiguration, IUnitBaseConfiguration } from "../../../../Models/IUnitConfiguration";
import { PropertyConstants } from "../../../../Models/Constants";
import { setTemporaryState } from "../../../../Services/Global";
import logger from '../../../../Services/LoggingService';

/**
 * API operations for UnitConfigurationEditor
 * 
 * CRITICAL FIX: Added explicit pheTypes property to payload to ensure PHE type modifications
 * are properly saved to the database. The issue was that pheTypes were being updated in UI state
 * but not explicitly included in the API payload, causing the backend to receive null/empty PHETypes.
 */

/**
 * Save the unit configuration
 */
export const createSaveConfiguration = (
  apiService: ApiService,
  unitConfig: IUnitBaseConfiguration,
  sectionCollection: IMultiSectionUnitConfiguration[],
  isEdit: boolean,
  isNewCreated: boolean,
  setIsNewCreated: (created: boolean) => void,
  setIsSaving: (saving: boolean) => void,
  setSaveMessage: (message?: string) => void,
  loadInitialData: () => Promise<void>,
  propSpecId?: string,
  userInputs?: string[],
  assignOperationTargetValue?: boolean
) => async () => {
  setIsSaving(true);
  
  try {
    // Prepare base configuration state
    const baseConfigState = prepareBaseConfigState(unitConfig);
    
    // Save each section
    for (const section of sectionCollection) {
      await saveSectionConfiguration(
        apiService,
        section,
        baseConfigState,
        sectionCollection.length,
        isEdit,
        isNewCreated,
        setIsNewCreated
      );
    }
    
    // Save property specifications for single-section configurations
    if (sectionCollection.length === 1) {
      await savePropertySpecifications(
        apiService,
        unitConfig,
        propSpecId,
        userInputs,
        assignOperationTargetValue
      );
    }
    
    // Update state and show success message
    if (unitConfig?.basicDetailsUpdated) {
      updateConfigBasicDetails(baseConfigState, unitConfig);
    }
    
    setTemporaryState("✅ Saved", setSaveMessage);
    await loadInitialData();
  } catch (error) {
    logger.error('Failed to save configuration', error as Error, null, {
      component: 'UnitConfigurationEditor',
      method: 'saveConfiguration'
    });
  } finally {
    setIsSaving(false);
  }
};

/**
 * Prepare the base configuration state for saving
 */
export const prepareBaseConfigState = (unitConfig: IUnitBaseConfiguration) => {
  // Just return the unitConfig as-is, like legacy does
  return unitConfig;
};

/**
 * Save a section configuration
 */
export const saveSectionConfiguration = async (
  apiService: ApiService,
  currentSection: IMultiSectionUnitConfiguration,
  baseConfig: any,
  sectionCount: number,
  isEdit: boolean,
  isNewCreated: boolean,
  setIsNewCreated: (created: boolean) => void
) => {
  // Set multi-section flags
  const isMultiSection = sectionCount > 1;
  const updatedSection = {
    ...currentSection,
    isMultiSection,
    sectionCount: sectionCount
  };
  
  // Prepare payload exactly like legacy - spread ALL properties first
  const payload = {
    ...baseConfig,                    // Spread ALL unitConfig properties (includes operation target fields)
    ...updatedSection,                // Spread ALL section properties
    properties: baseConfig?.properties,
    modelApiKeyName: baseConfig?.modelApiKeyName,
    modelEndpointUrl: baseConfig?.modelEndpointUrl,
    
    // CRITICAL FIX: Explicitly include PHE types to ensure they're sent to backend
    pheTypes: baseConfig?.pheTypes || [],
    
    // Handle original values for tracking changes (exactly like legacy)
    industry: baseConfig?.oldIndustry === undefined ? baseConfig?.industry : baseConfig?.oldIndustry,
    subIndustry: baseConfig?.oldSubIndustry === undefined ? baseConfig?.subIndustry : baseConfig?.oldSubIndustry,
    application: baseConfig?.oldApplication === undefined ? baseConfig?.application : baseConfig?.oldApplication,
    subApplication: baseConfig?.oldSubApplication === undefined ? baseConfig?.subApplication : baseConfig?.oldSubApplication,
    productLine: baseConfig?.oldProductLine === undefined ? baseConfig?.productLine : baseConfig?.oldProductLine,
    alfaLavalPosition: baseConfig?.oldAlfaLavalPosition === undefined ? baseConfig?.alfaLavalPosition : baseConfig?.oldAlfaLavalPosition,
    processStage: baseConfig?.oldProcessStage === undefined ? baseConfig?.processStage : baseConfig?.oldProcessStage,
    
    // Updated values (exactly like legacy)
    updatedIndustry: baseConfig?.industry,
    updatedSubIndustry: baseConfig?.subIndustry,
    updatedApplication: baseConfig?.application,
    updatedSubApplication: baseConfig?.subApplication,
    updatedAlfaLavalPosition: baseConfig?.alfaLavalPosition,
    updatedProductLine: baseConfig?.productLine,
    updatedProcessStage: baseConfig?.processStage,
    
    basicDetailsUpdated: baseConfig?.basicDetailsUpdated
  };
  
  try {
    // Debug logging to verify PHE types are included in payload
    logger.debug('Saving section configuration with payload', {
      sectionNumber: currentSection.sectionNumber,
      pheTypes: payload.pheTypes,
      pheTypesCount: payload.pheTypes?.length || 0,
      isEdit,
      isNewCreated
    }, {
      component: 'UnitConfigurationEditor',
      method: 'saveSectionConfiguration'
    });
    
    let response;
    
    if (!isEdit && !isNewCreated) {
      // Create new configuration
      response = await apiService.postAsync(payload, `settings/unitconfigurations`);
      if (response.ok) {
        setIsNewCreated(true);
      }
    } else {
      // Update existing configuration
      response = await apiService.putAsync(payload, `settings/unitconfigurations`);
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Couldn't save: ${errorText}`);
    }
    
    return true;
  } catch (error) {
    logger.error('Failed to save section configuration', error as Error, {
      sectionNumber: currentSection.sectionNumber,
      isMultiSection: sectionCount > 1
    }, {
      component: 'UnitConfigurationEditor',
      method: 'saveSectionConfiguration'
    });
    throw error;
  }
};

/**
 * Save property specifications
 */
export const savePropertySpecifications = async (
  apiService: ApiService,
  unitConfig: IUnitBaseConfiguration,
  propSpecId?: string,
  userInputs?: string[],
  assignOperationTargetValue?: boolean
) => {
  try {
    // Update operation target value in user inputs if needed
    const currentUserInputs = [...(userInputs || [])];
    const operationTargetValueIndex = currentUserInputs.indexOf("UnitOperationTargetValue");
    
    if (assignOperationTargetValue && operationTargetValueIndex === -1) {
      currentUserInputs.push("UnitOperationTargetValue");
    } else if (!assignOperationTargetValue && operationTargetValueIndex !== -1) {
      currentUserInputs.splice(operationTargetValueIndex, 1);
    }
    
    // Prepare payload
    const payload = {
      ...unitConfig,
      userInputProperties: currentUserInputs,
      id: propSpecId
    };
    
    let response;
    
    if (propSpecId) {
      // Update existing property specification
      response = await apiService.putAsync(
        payload, 
        `settings/unitPropertySpecifications/${propSpecId}`
      );
    } else {
      // Create new property specification
      response = await apiService.postAsync(
        payload, 
        "settings/unitPropertySpecifications"
      );
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Couldn't save property specifications: ${errorText}`);
    }
    
    return true;
  } catch (error) {
    logger.error('Failed to save property specifications', error as Error, {
      propSpecId,
      hasOperationTargetValue: assignOperationTargetValue
    }, {
      component: 'UnitConfigurationEditor',
      method: 'savePropertySpecifications'
    });
    throw error;
  }
};

/**
 * Update config basic details after saving
 */
export const updateConfigBasicDetails = (baseConfigState: any, unitConfig: IUnitBaseConfiguration) => {
  Object.assign(unitConfig, {
    industry: baseConfigState.updatedIndustry,
    subIndustry: baseConfigState.updatedSubIndustry,
    application: baseConfigState.updatedApplication,
    subApplication: baseConfigState.updatedSubApplication,
    alfaLavalPosition: baseConfigState.updatedAlfaLavalPosition,
    productLine: baseConfigState.updatedProductLine
  });
};

/**
 * Navigation functions
 */

/**
 * Go to property specification editor
 */
export const createGoToPropertySpecificationEditor = (
  navigate: (path: string, options?: any) => void,
  unitConfig: IUnitBaseConfiguration,
  sectionCount: number
) => (): void => {
  navigate(
    `/admin/unitconfigurationmanagement/propertyspecificationeditor/${unitConfig?.industry}/${unitConfig?.subIndustry}/${unitConfig?.application}/${unitConfig?.subApplication}/${unitConfig?.processStage}/${unitConfig?.productLine}/${unitConfig?.alfaLavalPosition}/${sectionCount}`, 
    { state: { unitConfiguration: unitConfig } }
  );
};

/**
 * Go to property range specification editor
 */
export const createGoToPropertyRangeSpecificationEditor = (
  navigate: (path: string, options?: any) => void,
  unitConfig: IUnitBaseConfiguration,
  sectionCollection: IMultiSectionUnitConfiguration[]
) => (): void => {
  let inputs = sectionCollection.find(c => c.sectionNumber == 1)?.inputs;
  inputs = inputs === undefined ? [] : inputs;
  inputs.push(PropertyConstants.TempApproachHotInlet);
  inputs.push(PropertyConstants.TempApproachHotOutlet);
  inputs.push(PropertyConstants.DeltaTemperatureColdSide);
  inputs.push(PropertyConstants.DeltaTemperatureHotSide);
  navigate(
    `/admin/unitconfigurationmanagement/propertyrangespecificationeditor/${unitConfig?.industry}/${unitConfig?.subIndustry}/${unitConfig?.application}/${unitConfig?.subApplication}/${unitConfig?.processStage}/${unitConfig?.productLine}/${unitConfig?.alfaLavalPosition}/${sectionCollection.length}/${inputs}`, 
    { state: { unitConfiguration: unitConfig } }
  );
}; 